import {
  Paper,
  Avatar,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { usePasswordResetSubmit } from "../../Query/useAuth";
import colors from "../../Constants/colors";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import Loading from "../Common/Loading";
import { ComoInput } from "../Common/ComoInput";

const PasswordResetSubmit = () => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const { mutate: passwordSubmitMutate, isPending: isLoading } =
    usePasswordResetSubmit();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    setToken(tokenParam);
  }, [searchParams]);

  const handleChangeNewPassword = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewPassword(event.target.value);
  };

  const handleChangeConfirmPassword = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(event.target.value);
  };

  const handlePasswordResetSubmit = (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();
    if (newPassword === confirmPassword) {
      passwordSubmitMutate(
        { password: newPassword, token },
        {
          onSuccess: () => {
            navigate("/login");
          },
          onError: () => {
            setToken(null);
          },
        }
      );
    } else {
      enqueueSnackbar("Passwords Must Match!", { variant: "error" });
    }
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={colors.gray100}
        color1={colors.accent}
        color2={colors.primary_dark}
      />
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        minHeight: "100vh",
        overflow: "hidden",
        backgroundColor: colors.gray50,
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: 3,
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.primary_dark}`,
            backgroundColor: colors.white,
            maxWidth: { xs: "100%", sm: 480 },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Avatar
              sx={{
                width: 72,
                height: 72,
                backgroundColor: colors.white,
                marginBottom: 2,
                border: `2px solid ${colors.primary_dark}`,
              }}
            >
              <img
                src="/commo_logo.png"
                alt="Logo"
                style={{ width: "100%", height: "auto" }}
              />
            </Avatar>
            <Typography
              variant="h4"
              sx={{
                color: colors.primary_dark,
                fontWeight: 600,
                textAlign: "center",
                mb: 1,
              }}
            >
              Connected Innovation Hub
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.gray600, textAlign: "center" }}
            >
              {token
                ? "Set a new password for your account."
                : "Invalid or missing reset token."}
            </Typography>
          </Box>

          {token ? (
            <form onSubmit={handlePasswordResetSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <ComoInput
                  label="New password"
                  type={showNewPassword ? "text" : "password"}
                  required
                  value={newPassword}
                  onChange={handleChangeNewPassword}
                  disabled={isLoading}
                  primaryColor={colors.primary_dark}
                  multiline={false}
                  maxRows={1}
                  size="medium"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowNewPassword}
                          edge="end"
                          sx={{ color: colors.gray600 }}
                        >
                          {showNewPassword ? (
                            <Visibility sx={{ fontSize: 20 }} />
                          ) : (
                            <VisibilityOff sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: colors.gray50,
                      "&.Mui-focused": {
                        backgroundColor: colors.white,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: colors.gray100,
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: `${colors.primary_dark} !important`,
                      "&::placeholder": {
                        color: `${colors.gray400} !important`,
                        opacity: 1,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: `${colors.gray600} !important`,
                      "&.Mui-focused": {
                        color: `${colors.primary_dark} !important`,
                      },
                    },
                  }}
                />

                <ComoInput
                  label="Confirm new password"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={handleChangeConfirmPassword}
                  disabled={isLoading}
                  primaryColor={colors.primary_dark}
                  multiline={false}
                  maxRows={1}
                  size="medium"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                          sx={{ color: colors.gray600 }}
                        >
                          {showConfirmPassword ? (
                            <Visibility sx={{ fontSize: 20 }} />
                          ) : (
                            <VisibilityOff sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: colors.gray50,
                      "&.Mui-focused": {
                        backgroundColor: colors.white,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: colors.gray100,
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: `${colors.primary_dark} !important`,
                      "&::placeholder": {
                        color: `${colors.gray400} !important`,
                        opacity: 1,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: `${colors.gray600} !important`,
                      "&.Mui-focused": {
                        color: `${colors.primary_dark} !important`,
                      },
                    },
                  }}
                />

                <Button
                  type="submit"
                  variant="modern"
                  fullWidth
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 1.5,
                    mt: 1,
                  }}
                >
                  Set new password
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate("/login")}
                  sx={{
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 1.5,
                    borderColor: colors.gray200,
                    color: colors.primary_dark,
                    backgroundColor: colors.white,
                    "&:hover": {
                      borderColor: colors.primary_dark,
                      backgroundColor: colors.gray50,
                    },
                  }}
                >
                  Back to login
                </Button>
              </Box>
            </form>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Typography
                variant="body2"
                sx={{
                  color: colors.gray600,
                  textAlign: "center",
                  mb: 1,
                }}
              >
                Invalid or expired token. Please request a new password reset
                link.
              </Typography>
              <Button
                variant="modern"
                fullWidth
                onClick={() => navigate("/password-reset-request")}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 1.5,
                }}
              >
                Request new link
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate("/login")}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  borderColor: colors.gray200,
                  color: colors.primary_dark,
                  backgroundColor: colors.white,
                  "&:hover": {
                    borderColor: colors.primary_dark,
                    backgroundColor: colors.gray50,
                  },
                }}
              >
                Back to login
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      <Box
        sx={{
          position: "fixed",
          left: 20,
          bottom: 20,
          zIndex: 1100,
          display: { xs: "none", md: "block" },
        }}
      >
        <img
          src="/logo_big_blue.png"
          alt="Commo Logo"
          style={{ maxHeight: "50px" }}
        />
      </Box>
    </Box>
  );
};

export default PasswordResetSubmit;
